/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { Filter } from '../../../model/Filter';
import { Metrics } from '../../../model/Metrics';
import { BaseController } from '../../BaseController';

class DashboardController extends BaseController {
    /** @ngInject */
    constructor(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log, $translate,
        authenticationService, statisticsService, domainService, siteService, equipmentService, filterService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.authenticationService = authenticationService;
        this.statisticsService = statisticsService;
        this.domainService = domainService;
        this.siteService = siteService;
        this.equipmentService = equipmentService;
        this.filterService = filterService;
        this.$translate = $translate;
        this.$log.debug('Controller \'Dashboard\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.metrics = new Metrics();
        this.commands = [];
        this.initFilters();

        this.handleRestServiceCall(this.loadFilters(), () => {
            this.initCommands();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the filters available at the top of the view.
     */
    initFilters() {
        this.equipmentFilter = Filter.equalFilterEmpty('equipment.id', 'model.equipment.equipment', 'SELECT', []);

        this.siteFilter = Filter.equalFilterEmpty('equipment.site.id', 'model.site.site', 'SELECT_AUTOCOMPLETE', [],
            siteId => this.getFilterEquipments(siteId));

        this.domainFilter = Filter.equalFilterEmpty('equipment.site.domain.id', 'model.domain.domain', 'SELECT',
            [], domainId => this.getFilterSites(domainId));

        this.filters = [this.domainFilter, this.siteFilter, this.equipmentFilter];
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        // No command in the view.
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains()
            .then(() => this.getFilterSites(this.domainFilter.value))
            .then(() => this.getFilterEquipments(this.siteFilter.value));
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Gets the list of sites for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of sites.
     */
    getFilterSites(domainId) {
        return this.filterService.getOrClearFilterOptions(this.siteService, this.siteFilter, 'name',
            [Filter.equalFilter('domain.id', domainId)], domainId)
            .then(() => this.getFilterEquipments(this.siteFilter.value));
    }

    /**
     * Gets the list of equipments for a given site.
     *
     * @param {Number} [siteId] Site ID, may be undefined if no site is selected.
     * @return {Object[]} Promise providing an array of equipments.
     */
    getFilterEquipments(siteId) {
        return this.filterService.getOrClearFilterOptions(this.equipmentService, this.equipmentFilter, 'name',
            [Filter.equalFilter('site.id', siteId)], siteId);
    }

    /**
     * Gets the metrics.
     *
     * @return {Promise} A promise providing the metrics.
     */
    reload() {
        let promise;
        if (this.authenticationService.userHasRole(['ADMIN', 'METRICS'])) {
            promise = this.statisticsService.getMetrics(this.filterService.buildFilters(this.filters)).then(metrics => {
                this.metrics.grtRate.value = metrics.grtRate;
                this.metrics.equipmentAvailability.value = metrics.equipmentAvailability;
                this.metrics.failingEquipments.value = metrics.failingEquipments;
                this.metrics.actionRequestsByStatus.splice(0);
                const unsortRecords = {};
                for (const status in metrics.actionRequestsByStatus) {
                    if (metrics.actionRequestsByStatus.hasOwnProperty(status)) {
                        const record = {
                            label: `${status}`,
                            value: metrics.actionRequestsByStatus[status]
                        };
                        this.$translate(`model.actionrequest.status.${status}`).then(
                            translation => record.label = translation);
                        unsortRecords[status] = record;
                    }
                }
                for (const status of ['OPENED', 'COMPLETED', 'CLOSED', 'CANCELED']) {
                    this.metrics.actionRequestsByStatus.push(unsortRecords[status]);
                }
            });
        } else {
            promise = [];
        }
        return promise;
    }
}

export const dashboardComponent = {
    template: require('./dashboard.html'),
    controller: DashboardController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/statistics/component/Dashboard.js